import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { listUserGroups, findGroupBySlug, ensureOwnerMembership } from './api';
import { supabase } from '../supabase';

// Keep slug so we can match the current URL path without querying restricted tables
type GroupSummary = { group: { id: string; name: string; slug?: string | null; image_url?: string | null }; role: 'admin' | 'member' | 'owner' };

type GroupContextType = {
    groups: GroupSummary[];
    activeGroupId: string | null;
    setActiveGroupId: (id: string | null) => void;
    reload: () => Promise<void>;
    vaultSlug: string | null;
    accessState: 'unknown' | 'ok' | 'not-member' | 'missing';
};

const GroupContext = createContext<GroupContextType | undefined>(undefined);

export function GroupProvider({ children }: { children: React.ReactNode }) {
    const [groups, setGroups] = useState<GroupSummary[]>([]);
    const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
    const [vaultSlug, setVaultSlug] = useState<string | null>(null);
    const [accessState, setAccessState] = useState<'unknown' | 'ok' | 'not-member' | 'missing'>('unknown');

    const reload = async () => {
        try {
            // Only load groups if authenticated; otherwise policies may cast empty sub -> uuid error
            const { data: userData } = await supabase.auth.getUser();
            const isAuthed = !!userData?.user;
            const data = isAuthed ? await listUserGroups() : [];
            // Preserve slug and image_url to enable client-side matching and branding
            const summaries: GroupSummary[] = data.map(d => ({ group: { id: d.group.id, name: d.group.name, slug: (d.group as any).slug, image_url: (d.group as any).image_url }, role: d.role }));
            setGroups(summaries);
            // Determine vault slug from URL
            const firstSeg = (window.location.pathname.split('/').filter(Boolean)[0] || '').trim();
            // Treat certain paths as non-slug home (e.g., auth/reset, dev hosts)
            const reserved = new Set(['auth', 'login', 'reset-password', 'code', 'preview']);
            const pathSlug = firstSeg && !reserved.has(firstSeg.toLowerCase()) ? firstSeg : '';
            setVaultSlug(pathSlug || null);
            if (!pathSlug) {
                // No slug: do NOT auto-enter any vault. Stay in home experience.
                setActiveGroupId(null);
                setAccessState('ok');
                return;
            }

            // If not authenticated, don't hit DB for slug; wait until after login
            if (!isAuthed) {
                setActiveGroupId(null);
                setAccessState('unknown');
                return;
            }
            // Avoid querying the restricted vaults table by slug (RLS may deny non-members).
            // Instead, resolve access purely from the authenticated user's memberships we already fetched.
            const match = summaries.find(s => (s.group.slug || '').toLowerCase() === pathSlug.toLowerCase());
            if (match) {
                setActiveGroupId(match.group.id);
                setAccessState('ok');
                return;
            }
            // Fallback: If no membership matched the slug, try a direct lookup by slug.
            // If the user is actually a member/owner, RLS should allow selecting that vault row; if not, it will fail or return null.
            try {
                if (pathSlug) {
                    const bySlug = await findGroupBySlug(pathSlug);
                    if (bySlug && bySlug.id) {
                        const isMember = summaries.some(s => s.group.id === bySlug.id);
                        if (isMember) {
                            setActiveGroupId(bySlug.id);
                            setAccessState('ok');
                            return;
                        }
                        // If the authenticated user is the creator of this vault, they are implicitly the owner.
                        const userId = userData?.user?.id;
                        if (userId && (bySlug as any).created_by === userId) {
                            // No fallbacks: ensure owner membership via core; if it fails, surface error
                            await ensureOwnerMembership(bySlug.id, userId);
                            setActiveGroupId(bySlug.id);
                            setAccessState('ok');
                            return;
                        }
                        // Slug exists but current user has no membership
                        setActiveGroupId(null);
                        setAccessState('not-member');
                        return;
                    }
                }
            } catch (e) {
                // If RLS denies or any error occurs, conservatively treat as not a member
                setActiveGroupId(null);
                setAccessState('not-member');
                return;
            }
            // If no membership matches the slug, treat as no access. We cannot reliably distinguish
            // between a missing slug and no-membership without a privileged check, so prefer 'not-member'.
            setActiveGroupId(null);
            setAccessState('not-member');
        } catch (e: any) {
            const msg = (typeof e === 'string' ? e : e?.message || '').toString();
            const isConsoleInspector = msg.includes('ConsoleInspector') || msg.includes('Console Inspector');
            const isPreviewScript = msg.includes('/.preview-scri');
            if (isConsoleInspector || isPreviewScript) {
                console.debug('Suppressed preview/inspector noise in reload');
            } else {
                console.error('Failed to load groups', e);
            }
            setGroups([]);
            setActiveGroupId(null);
            setAccessState('unknown');
        }
    };

    useEffect(() => {
        reload();
    }, []);

    // Reload on auth changes so slug access is re-evaluated after login/logout
    useEffect(() => {
        const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
            reload();
        });
        return () => subscription.unsubscribe();
    }, []);

    const value = useMemo(() => ({ groups, activeGroupId, setActiveGroupId, reload, vaultSlug, accessState }), [groups, activeGroupId, vaultSlug, accessState]);
    return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>;
}

export function useGroupContext() {
    const ctx = useContext(GroupContext);
    if (!ctx) throw new Error('useGroupContext must be used within GroupProvider');
    return ctx;
}
